import { LOGO } from "@/assets/images";
import { EyeOffSvgIcon, EyeSvgIcon } from "@/assets/svg";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useResetPasswordMutation } from "@/redux/services/auth/authApi";
import type { RootStateType } from "@/redux/store";
import routesPath from "@/utils/routesPath";
import { resetPasswordSchema } from "@/utils/schemas/auth";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

interface ResetPasswordFormValues {
  email: string;
  password: string;
  password_confirmation: string;
  token: string;
}

const { LOGIN, DASHBOARD } = routesPath;

const ResetPassword = () => {
  const user = useSelector((state: RootStateType) => state.auth.user);
  const [showPassword, setShowPassword] = useState({
    password: false,
    confirmPassword: false,
  });

  const [resetPassword, { isLoading }] = useResetPasswordMutation();

  const navigate = useNavigate();
  const searchParams = useSearchParams();
  const token = searchParams[0].get("token");
  const email = searchParams[0].get("email");

  const handleSubmit = (values: ResetPasswordFormValues) => {
    resetPassword(values)
      .unwrap()
      .then((res: { success: boolean; message: string }) => {
        if (res?.success === true) {
          navigate(DASHBOARD);
          toast.success(res?.message);
        } else {
          toast.error(res?.message);
        }
      })
      .catch((error) => {
        const errorMessage =
          (error as { data?: { message?: string } })?.data?.message ||
          "Failed to reset password";
        toast.error(errorMessage);
      });
  };

  const formik = useFormik({
    initialValues: {
      email: email || "",
      password: "",
      password_confirmation: "",
      token: token || "",
    },
    validationSchema: resetPasswordSchema,
    onSubmit: handleSubmit,
  });

  useEffect(() => {
    if (user && Object.keys(user).length > 0) {
      navigate(DASHBOARD);
    }

    if (!token || !email) {
      navigate(LOGIN);
    }
  }, [user, token, email, navigate]);

  return (
    <div className="min-h-screen bg-[#F9F9F9] flex sm:items-center sm:justify-center p-4">
      <div className=" sm:bg-[#FFFFFF] sm:rounded-lg sm:shadow-sm px-4 pt-10 sm:p-8 w-full max-w-[793px] sm:px-[155px] sm:py-[64px]">
        <div className="flex justify-center mb-6">
          <img
            src={LOGO}
            alt="logo"
            className="h-[30px] w-[136px] md:w-[179px] max-w-[179px] md:h-[43px] md:max-h-[43px]"
          />
        </div>

        <div className="mb-10">
          <h1 className="text-[24px]  md:text-[34px] font-semibold text-[#4C4C4C] md:w-[364px]">
            Create a new <br className="sm:hidden" /> password
          </h1>
          <p className="text-[#6B7280] text-[12px] max-w-[275px] md:max-w-full md:text-sm font-[400] DM-Sans">
            Choose a strong password to help protect your account.
          </p>
        </div>

        <form onSubmit={formik.handleSubmit} className="">
          <div className="space-y-[23px]">
            <div className="relative">
              <Input
                id="password"
                name="password"
                label="Password"
                type={showPassword.password ? "text" : "password"}
                placeholder="Enter 8 character password"
                value={formik.values.password}
                onChange={formik.handleChange}
                error={formik.errors.password}
                touched={formik.touched.password}
                containerClass=""
              />
              <button
                type="button"
                className="absolute right-3 top-8 md:top-10.5 text-gray-400 hover:text-gray-600 cursor-pointer"
                onClick={() =>
                  setShowPassword({
                    ...showPassword,
                    password: !showPassword.password,
                  })
                }
              >
                {showPassword.password ? (
                  <EyeSvgIcon className="w-[16px] h-[16px] text-gray-400 hover:text-gray-600" />
                ) : (
                  <EyeOffSvgIcon className="w-[16px] h-[16px] text-gray-400 hover:text-gray-600" />
                )}
              </button>
            </div>
            <div className="relative">
              <Input
                id="password_confirmation"
                name="password_confirmation"
                label="Confirm Password"
                type={showPassword.confirmPassword ? "text" : "password"}
                placeholder="Confirm password"
                value={formik.values.password_confirmation}
                onChange={formik.handleChange}
                error={formik.errors.password_confirmation}
                touched={formik.touched.password_confirmation}
                containerClass=""
              />
              <button
                type="button"
                className="absolute right-3 top-8 md:top-10.5 text-gray-400 hover:text-gray-600 cursor-pointer"
                onClick={() =>
                  setShowPassword({
                    ...showPassword,
                    confirmPassword: !showPassword.confirmPassword,
                  })
                }
              >
                {showPassword.confirmPassword ? (
                  <EyeSvgIcon className="w-[16px] h-[16px] text-gray-400 hover:text-gray-600" />
                ) : (
                  <EyeOffSvgIcon className="w-[16px] h-[16px] text-gray-400 hover:text-gray-600" />
                )}
              </button>
            </div>

            <Button
              type="submit"
              className="w-full hover:bg-primary/90 text-white font-medium py-3 rounded-[6px]"
              disabled={isLoading}
              loading={isLoading}
              // loadingText='Logging in...'
            >
              Submit
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
